// Design System - Consistent Visual Language for Tour in a Box

import { BRAND } from './tourUtils';

// ============================================
// COLOR PALETTE
// ============================================

export const colors = {
  // Primary brand colors
  primary: {
    main: BRAND.primary,      // #B35C3F - warm terracotta
    light: '#C87B61',
    dark: '#8B4830',
    contrast: '#FFFFFF',
  },

  // Accent colors
  accent: {
    main: BRAND.accent,        // #38B6FF - bright blue
    light: '#5CC4FF',
    dark: '#2A8FCC',
    contrast: '#FFFFFF',
  },

  // Semantic colors
  success: {
    main: '#10B981',           // Green
    light: '#34D399',
    dark: '#059669',
    bg: '#D1FAE5',
  },

  warning: {
    main: '#F59E0B',           // Amber
    light: '#FBBF24',
    dark: '#D97706',
    bg: '#FEF3C7',
  },

  error: {
    main: '#EF4444',           // Red
    light: '#F87171',
    dark: '#DC2626',
    bg: '#FEE2E2',
  },

  // Neutrals
  neutral: {
    white: '#FFFFFF',
    black: '#000000',
    50: '#F9FAFB',
    100: '#F3F4F6',
    200: '#E5E7EB',
    300: '#D1D5DB',
    400: '#9CA3AF',
    500: '#6B7280',
    600: '#4B5563',
    700: '#374151',
    800: '#1F2937',
    900: '#111827',
  },

  // Special effects
  treasure: {
    gold: '#F1C40F',
    amber: '#F39C12',
    bronze: '#CD7F32',
  },
};

// ============================================
// ELEVATION / SHADOW SYSTEM
// ============================================

export const elevation = {
  none: 'none',

  xs: '0 1px 2px 0 rgba(0, 0, 0, 0.05)',

  sm: '0 1px 3px 0 rgba(0, 0, 0, 0.1), 0 1px 2px -1px rgba(0, 0, 0, 0.1)',

  md: '0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -2px rgba(0, 0, 0, 0.1)',

  lg: '0 10px 15px -3px rgba(0, 0, 0, 0.1), 0 4px 6px -4px rgba(0, 0, 0, 0.1)',

  xl: '0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 8px 10px -6px rgba(0, 0, 0, 0.1)',

  '2xl': '0 25px 50px -12px rgba(0, 0, 0, 0.25)',

  // Colored shadows for emphasis
  glow: {
    primary: `0 0 20px ${colors.primary.main}40`,
    accent: `0 0 20px ${colors.accent.main}40`,
    treasure: `0 6px 18px rgba(245, 197, 66, 0.55)`,
  },

  // Inner shadows for depth
  inner: 'inset 0 2px 4px 0 rgba(0, 0, 0, 0.06)',
};

// ============================================
// BORDER RADIUS SYSTEM
// ============================================

export const radius = {
  none: '0',
  sm: '0.375rem',      // 6px
  md: '0.5rem',        // 8px
  lg: '0.75rem',       // 12px
  xl: '1rem',          // 16px
  '2xl': '1.25rem',    // 20px
  '3xl': '1.5rem',     // 24px
  full: '9999px',      // Perfect circle/pill

  // Semantic naming
  button: '1.25rem',   // 2xl - 20px
  card: '1rem',        // xl - 16px
  modal: '0.75rem',    // lg - 12px
  input: '0.75rem',    // lg - 12px
};

// ============================================
// TYPOGRAPHY SYSTEM
// ============================================

export const typography = {
  fontFamily: {
    primary: "'Plus Jakarta Sans', Inter, system-ui, -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, 'Helvetica Neue', Arial, sans-serif",
    special: "'Special Elite', 'Courier New', monospace",
    display: "'Crimson Pro', Georgia, serif",
  },

  fontSize: {
    xs: '0.75rem',       // 12px
    sm: '0.875rem',      // 14px
    base: '1rem',        // 16px
    lg: '1.125rem',      // 18px
    xl: '1.25rem',       // 20px
    '2xl': '1.5rem',     // 24px
    '3xl': '1.875rem',   // 30px
    '4xl': '2.25rem',    // 36px
    '5xl': '3rem',       // 48px
  },

  fontWeight: {
    normal: '400',
    medium: '500',
    semibold: '600',
    bold: '700',
    extrabold: '800',
  },

  lineHeight: {
    tight: '1.25',
    normal: '1.5',
    relaxed: '1.75',
  },

  letterSpacing: {
    tight: '-0.025em',
    normal: '0',
    wide: '0.025em',
  },
};

// ============================================
// SPACING SYSTEM (4px base unit)
// ============================================

export const spacing = {
  0: '0',
  1: '0.25rem',    // 4px
  2: '0.5rem',     // 8px
  3: '0.75rem',    // 12px
  4: '1rem',       // 16px
  5: '1.25rem',    // 20px
  6: '1.5rem',     // 24px
  8: '2rem',       // 32px
  10: '2.5rem',    // 40px
  12: '3rem',      // 48px
  16: '4rem',      // 64px
  20: '5rem',      // 80px
  24: '6rem',      // 96px
};

// ============================================
// TRANSITIONS & ANIMATIONS
// ============================================

export const transitions = {
  duration: {
    fast: '150ms',
    normal: '250ms',
    slow: '350ms',
  },

  timing: {
    ease: 'cubic-bezier(0.4, 0, 0.2, 1)',
    easeIn: 'cubic-bezier(0.4, 0, 1, 1)',
    easeOut: 'cubic-bezier(0, 0, 0.2, 1)',
    easeInOut: 'cubic-bezier(0.4, 0, 0.2, 1)',
    spring: 'cubic-bezier(0.34, 1.56, 0.64, 1)',
  },

  // Common transition strings
  default: 'all 250ms cubic-bezier(0.4, 0, 0.2, 1)',
  transform: 'transform 250ms cubic-bezier(0.4, 0, 0.2, 1)',
  opacity: 'opacity 250ms cubic-bezier(0.4, 0, 0.2, 1)',
  color: 'color 150ms cubic-bezier(0.4, 0, 0.2, 1)',
};

// ============================================
// Z-INDEX LAYERS
// ============================================

export const zIndex = {
  base: 0,
  dropdown: 1000,
  sticky: 1020,
  fixed: 1030,
  modalBackdrop: 1040,
  modal: 1050,
  popover: 1060,
  tooltip: 1070,
};

// ============================================
// COMPONENT VARIANTS
// ============================================

export const variants = {
  button: {
    primary: {
      background: colors.primary.main,
      color: colors.primary.contrast,
      hover: colors.primary.dark,
      shadow: elevation.sm,
    },
    accent: {
      background: colors.accent.main,
      color: colors.accent.contrast,
      hover: colors.accent.dark,
      shadow: elevation.sm,
    },
    treasure: {
      background: `linear-gradient(135deg, ${colors.treasure.gold} 0%, ${colors.treasure.amber} 100%)`,
      color: colors.neutral.black,
      hover: colors.treasure.amber,
      shadow: elevation.glow.treasure,
    },
    ghost: {
      background: 'transparent',
      color: colors.neutral[700],
      hover: colors.neutral[100],
      shadow: elevation.none,
    },
    outline: {
      background: 'transparent',
      color: colors.neutral[700],
      hover: colors.neutral[50],
      border: colors.neutral[300],
      shadow: elevation.none,
    },
  },

  card: {
    default: {
      background: colors.neutral.white,
      border: colors.neutral[200],
      shadow: elevation.md,
    },
    glass: {
      background: 'rgba(255, 255, 255, 0.8)',
      backdropFilter: 'blur(12px)',
      border: 'rgba(255, 255, 255, 0.2)',
      shadow: elevation.lg,
    },
    elevated: {
      background: colors.neutral.white,
      border: 'transparent',
      shadow: elevation.xl,
    },
  },
};

// ============================================
// HELPER FUNCTIONS
// ============================================

/**
 * Get elevation style object
 */
export const getElevation = (level = 'md') => ({
  boxShadow: elevation[level] || elevation.md,
});

/**
 * Get border radius style object
 */
export const getRadius = (size = 'md') => ({
  borderRadius: radius[size] || radius.md,
});

/**
 * Get transition style
 */
export const getTransition = (property = 'all', duration = 'normal') => ({
  transition: `${property} ${transitions.duration[duration]} ${transitions.timing.ease}`,
});

/**
 * Create glass morphism effect
 */
export const glassEffect = (opacity = 0.8) => ({
  background: `rgba(255, 255, 255, ${opacity})`,
  backdropFilter: 'blur(12px)',
  WebkitBackdropFilter: 'blur(12px)',
  border: `1px solid rgba(255, 255, 255, ${opacity * 0.25})`,
});

// Export all as default for convenience
const designSystem = {
  colors,
  elevation,
  radius,
  typography,
  spacing,
  transitions,
  zIndex,
  variants,
  getElevation,
  getRadius,
  getTransition,
  glassEffect,
};

export default designSystem;
